import * as React from 'react';
import { Box, Typography } from '@mui/material';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { LoginState } from './LoginModal';
import { OverlineText } from '../../shared/typography/PrimaryTypography';
import { MFA_TIMEOUT_MS } from '../../../env';

/**
 * Handles the selection of which two-factor authentication method the user wants to register.
 * @param {(login_state: LoginState): void} handleLoginState Handles the changing of modal state to various parts of the login flow.
 * @param {(): void} closeLogin Closes the sign in process completely and resets it to initial sign in step.
 * @returns {JSX.Element}
 */
export const MfaMethodSelector = ({
  handleLoginState,
  closeLogin
}: {
  handleLoginState: (login_state: LoginState) => void;
  closeLogin: () => void;
}): JSX.Element => {
  const [loadingMfa, setLoadingMfa] = React.useState<boolean>(false);

  // Handles switching to enrollment of SMS MFA flow.
  const handleSmsSelection = async (event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    setLoadingMfa(true);
    handleLoginState('enroll_sms_mfa');
    setLoadingMfa(false);
  };

  // Handles switching to enrollment of Authenticator MFA flow.
  const handleAuthenticatorSelection = async (event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    setLoadingMfa(true);
    handleLoginState('enroll_authenticator_mfa');
    setLoadingMfa(false);
  };

  // Handles closing of the MFA enrollment before it times out to force user to restart process
  React.useEffect(() => {
    const timeout_id: NodeJS.Timeout = setTimeout(async () => {
      closeLogin();
    }, MFA_TIMEOUT_MS);
    return () => clearTimeout(timeout_id);
  }, []);

  return (
    <Box>
      <Typography sx={{ textAlign: 'center', color: '#053d5d', my: 1, fontSize: '0.9rem' }}>
        This application requires two-factor authentication. Please select one of the options below
        as your preferred method of authentication.
      </Typography>
      <form>
        <PrimaryButton
          data-testid="sms-mfa-button"
          sx={{ mt: 4, width: '100%' }}
          onClick={handleSmsSelection}
          id="sign-in-button"
          type="submit"
          loading={loadingMfa}
        >
          SMS Based
        </PrimaryButton>
        <PrimaryButton
          data-testid="authenticator-mfa-button"
          sx={{ mt: 4, width: '100%' }}
          onClick={handleAuthenticatorSelection}
          id="sign-in-button"
          type="submit"
          loading={loadingMfa}
        >
          Authenticator-App Based
        </PrimaryButton>
        <OverlineText align="center" sx={{ mt: 2 }}>
          (Requires user to have one of the following Apps: Google Authenticator, Microsoft
          Authenticator, LastPass Authenticator, etc.)
        </OverlineText>
      </form>
    </Box>
  );
};
